import { logger } from 'common/services/logger';

import { narrowEventStatus } from './natalieCreateV2.mappers';
import {
  type AccountlessNatalieEventV2ApiResponse,
  type NatalieEventPublicDetail,
  type NatalieEventListItem,
  type ExploreUseCase,
  type Persona,
  NatalieEventStatus,
} from './natalieEvent.types';

import type { NatalieEventV2Dto, NatalieEventV2PageDto } from './natalieCreateV2.types';
import type { PaginatedApiResponse } from 'domain/Common';

const isNatalieEventStatus = (value: string): value is NatalieEventStatus =>
  value === NatalieEventStatus.Active || value === NatalieEventStatus.Completed;

/** The raw stored status an older accountless deployment reports for an ended
 *  event. The API now maps this to 'COMPLETED' itself (natalie-api#118), matching
 *  the JWT-authenticated endpoints, so this is back-compat only. */
const ACCOUNTLESS_DB_CLOSED = 'CLOSED';

/**
 * Narrow the accountless status onto the ACTIVE | COMPLETED domain type.
 *
 * The 'CLOSED' branch is deliberately kept even though the API no longer sends it:
 * this bundle can be live before the API deploy lands, and the fallback below is
 * fail-open (Active), which would put a live join CTA on an ended conversation.
 * Mapping it costs one comparison and makes the two deploys order-independent.
 */
const narrowAccountlessStatus = (value: string): NatalieEventStatus => {
  if (isNatalieEventStatus(value)) return value;
  if (value === ACCOUNTLESS_DB_CLOSED) return NatalieEventStatus.Completed;
  logger.warn(`[mapAccountlessV2EventFromApi] Unknown status "${value}", defaulting to Active`);
  return NatalieEventStatus.Active;
};

// Default a missing/legacy persona (events created before the field existed) to
// { type: 'user' }, and defend the boundary against any unexpected runtime value.
const narrowPersona = (persona: Persona | null | undefined): Persona => {
  if (persona?.type === 'user' || persona?.type === 'buyer') return { type: persona.type };
  if (persona !== undefined && persona !== null) {
    logger.warn('[natalie mappers] Unexpected persona, defaulting to user:', { persona });
  }
  return { type: 'user' };
};

// Default a missing/legacy exploreUseCases (events created before the field existed,
// or non-array runtime values) to []. The backend is authoritative on card content,
// so this is a light shape guard: pass through well-formed { icon, title, body }
// objects and drop malformed entries defensively rather than enforce an allowlist.
const isWellFormedExploreUseCase = (item: unknown): item is ExploreUseCase => {
  if (typeof item !== 'object' || item === null) return false;
  const candidate = item as Record<string, unknown>;
  return (
    typeof candidate.icon === 'string' && typeof candidate.title === 'string' && typeof candidate.body === 'string'
  );
};

const narrowExploreUseCases = (exploreUseCases: ExploreUseCase[] | null | undefined): ExploreUseCase[] => {
  if (Array.isArray(exploreUseCases)) {
    const wellFormed = exploreUseCases.filter(isWellFormedExploreUseCase);
    if (wellFormed.length !== exploreUseCases.length) {
      logger.warn('[natalie mappers] Dropped malformed exploreUseCases entries:', {
        received: exploreUseCases.length,
        kept: wellFormed.length,
      });
    }
    return wellFormed;
  }
  if (exploreUseCases !== undefined && exploreUseCases !== null) {
    logger.warn('[natalie mappers] Unexpected exploreUseCases, defaulting to []:', { exploreUseCases });
  }
  return [];
};

/**
 * The authed v2 event read, mapped for the registered landing page.
 *
 * Serves both faces. `GET /v2/natalie/{eventId}` and `.../draft` answer the same shape — the
 * draft adds one property the landing never reads — so which one was fetched is the caller's
 * decision and makes no difference here.
 *
 * `videoUrl`, `exploreUseCases` and the duration come out of the configuration blocks rather
 * than off the top level, which is the whole difference between the two contract generations:
 * v1 was flat, v2 groups what a published version freezes.
 */
export const mapNatalieV2PublicEventFromApi = (data: NatalieEventV2Dto): NatalieEventPublicDetail => ({
  id: data.id,
  communityId: data.community.id,
  endDate: data.eventConfig.endDate ?? '',
  name: data.eventConfig.name ?? '',
  topic: data.eventConfig.topic ?? '',
  description: data.eventConfig.description ?? null,
  ownerName: data.ownerName ?? '',
  communityName: data.community.name ?? '',
  // The v2 read is the only one that can answer `DRAFT`, so it narrows against the v2 domain.
  // `narrowAccountlessStatus` exists for the two-value accountless shapes and would fold a draft
  // into `ACTIVE`, reporting a never-published event as a live one.
  status: narrowEventStatus(data.status),
  durationInMinutes: data.agentConfig.sessionDurationMinutes ?? 0,
  communityLogo: data.community.logoImageLink ?? null,
  videoUrl: data.eventConfig.videoUrl ?? null,
  persona: narrowPersona(data.agentConfig.persona),
  exploreUseCases: narrowExploreUseCases(data.eventConfig.exploreUseCases),
  testInviteCode: data.testInviteCode ?? null,
  joinLimitReached: data.joinLimitReached ?? false,
});

/**
 * One page of the sessions list, flattened onto the row the table renders.
 *
 * `eventConfig` holds the published values once an event has been published and the draft's
 * before that, which is what a list wants: a session that has never gone live still shows the
 * name its organiser gave it rather than an empty cell.
 *
 * `status` passes through untouched — `DRAFT` included — as it now does on the single-event read
 * above. Collapsing a draft into ACTIVE here would mean the filter that asks for drafts gets rows
 * that do not say they are one.
 */
export const mapNatalieV2EventListFromApi = (
  page: NatalieEventV2PageDto
): PaginatedApiResponse<NatalieEventListItem> => ({
  totalCount: page.totalCount,
  data: page.data.map((event) => ({
    id: event.id,
    name: event.eventConfig.name ?? '',
    status: event.status,
    createdAt: event.createdAt,
    endDate: event.eventConfig.endDate,
    inviteCode: event.inviteCode ?? null,
    testInviteCode: event.testInviteCode ?? null,
    reportId: event.reportId,
    reportStatus: event.reportStatus,
  })),
});

/**
 * The accountless v2 participant read, mapped to the same shape the page already consumes.
 *
 * What v2 serves is the PUBLISHED version. A draft edit does not appear here until it is
 * published, which is the point of the split; an event that has never been published answers
 * 404 and is indistinguishable from an unknown id.
 */
export const mapAccountlessV2EventFromApi = (data: AccountlessNatalieEventV2ApiResponse): NatalieEventPublicDetail => ({
  id: data.id,
  communityId: data.community.id,
  endDate: data.endDate,
  name: data.name,
  topic: data.topic,
  description: data.description ?? null,
  ownerName: data.ownerName ?? '',
  communityName: data.community.name ?? '',
  status: narrowAccountlessStatus(data.status),
  durationInMinutes: data.sessionDurationMinutes ?? 0,
  communityLogo: data.community.logoImageLink ?? null,
  videoUrl: data.videoUrl ?? null,
  persona: narrowPersona(data.persona),
  exploreUseCases: narrowExploreUseCases(data.exploreUseCases),
  testInviteCode: data.testInviteCode ?? null,
  joinLimitReached: data.joinLimitReached ?? false,
});
